import { supabase } from '../lib/supabase.js';

export class SmsManager {
  constructor() {
    this.conversations = [];
    this.currentMessages = [];
    this.selectedConversationId = null;
    this.isLoading = false;
    this.conversationsSubscription = null;
    this.messagesSubscription = null;
    this.filterStatus = 'open'; // Default filter
    this.sortBy = 'last_message_at'; // Default sort
    this.sortOrder = 'desc'; // Default order
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow h-screen max-h-screen overflow-hidden">
        <div class="flex h-full">
          <!-- SMS Conversations List -->
          <div class="w-1/3 border-r border-gray-200 flex flex-col">
            <div class="p-4 border-b border-gray-200 bg-gray-50">
              <div class="flex justify-between items-center">
                <h2 class="text-lg font-bold text-gray-900">SMS Conversations</h2>
                <button 
                  id="refresh-sms-btn" 
                  class="bg-violet-600 text-white px-3 py-1 rounded text-sm hover:bg-violet-700"
                >
                  Refresh
                </button>
              </div>
              <p class="text-sm text-gray-600 mt-1">Two-way SMS communication with clients</p>
            </div>
            
            <!-- Filters and Sorts -->
            <div class="p-4 border-b border-gray-200 bg-gray-50 space-y-2">
              <div>
                <label for="sms-filter-status" class="block text-xs font-medium text-gray-700">Filter by Status</label>
                <select id="sms-filter-status" class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div class="flex space-x-2">
                <div class="flex-1">
                  <label for="sms-sort-by" class="block text-xs font-medium text-gray-700">Sort By</label>
                  <select id="sms-sort-by" class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                    <option value="last_message_at">Last Message</option>
                    <option value="created_at">Created At</option>
                  </select>
                </div>
                <div class="flex-1">
                  <label for="sms-sort-order" class="block text-xs font-medium text-gray-700">Order</label>
                  <select id="sms-sort-order" class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- New SMS Button -->
            <div class="p-4 border-b border-gray-200">
              <button 
                id="new-sms-btn" 
                class="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                📱 New SMS Conversation
              </button>
            </div>

            <!-- Conversations List -->
            <div id="sms-conversations-list" class="flex-1 overflow-y-auto">
              <div class="p-4 text-center text-gray-500">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                Loading SMS conversations...
              </div>
            </div>
          </div>

          <!-- SMS Messages Area -->
          <div class="flex-1 flex flex-col">
            <!-- SMS Header -->
            <div id="sms-header" class="p-4 border-b border-gray-200 bg-gray-50 hidden">
              <div class="flex justify-between items-center">
                <div id="sms-contact-info">
                  <!-- Contact info will be populated here -->
                </div>
                <div class="flex space-x-2">
                  <button 
                    id="close-conversation-btn" 
                    class="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 disabled:opacity-50"
                    title="Close this SMS conversation"
                  >
                    Close
                  </button>
                  <button 
                    id="archive-conversation-btn" 
                    class="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 disabled:opacity-50"
                    title="Archive this SMS conversation"
                  >
                    Archive
                  </button>
                </div>
              </div>
            </div>

            <!-- Messages Area -->
            <div id="sms-messages-area" class="flex-1 overflow-y-auto p-4 bg-gray-50">
              <div class="flex items-center justify-center h-full text-gray-500">
                <div class="text-center">
                  <div class="text-lg mb-2">📱</div>
                  <div>Select a conversation to view SMS messages</div>
                </div>
              </div>
            </div>

            <!-- Message Input Area -->
            <div id="sms-message-input-area" class="p-4 border-t border-gray-200 bg-white hidden">
              <div class="flex space-x-3">
                <textarea 
                  id="sms-message-input" 
                  rows="2" 
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  placeholder="Type your SMS message here..."
                ></textarea>
                <button 
                  id="send-sms-btn" 
                  class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700 disabled:opacity-50 self-start"
                  disabled
                >
                  Send SMS
                </button>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-xs text-gray-500">
                  <span id="sms-char-count">0</span> characters
                </div>
                <div class="text-xs text-gray-500">
                  Press Enter to send, Shift+Enter for new line
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- New SMS Modal -->
        <div id="new-sms-modal" class="hidden fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div class="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div class="mt-3">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Start New SMS Conversation</h3>
              
              <form id="new-sms-form" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input 
                    type="tel" 
                    id="new-sms-phone" 
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="e.g., 1234567890"
                  >
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea 
                    id="new-sms-message" 
                    required
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="Type your message here..."
                  ></textarea>
                  <div class="text-xs text-gray-500 mt-1">
                    <span id="new-sms-char-count">0</span> characters
                  </div>
                </div>
                
                <div class="flex space-x-3">
                  <button 
                    type="submit" 
                    class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Send SMS
                  </button>
                  <button 
                    type="button" 
                    id="cancel-new-sms-btn"
                    class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Success/Error Messages -->
        <div id="sms-notification" class="hidden fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg max-w-sm">
          <div id="sms-notification-content"></div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    // Set initial filter/sort values in UI
    document.getElementById('sms-filter-status').value = this.filterStatus;
    document.getElementById('sms-sort-by').value = this.sortBy;
    document.getElementById('sms-sort-order').value = this.sortOrder;

    // Make the manager globally accessible for notification callbacks
    window.smsManager = this;

    await this.loadConversations();
    
    // Start real-time subscriptions
    this.subscribeToConversations();

    // Refresh button
    document.getElementById('refresh-sms-btn').addEventListener('click', () => {
      this.loadConversations();
    });

    // Filter and sort change events
    document.getElementById('sms-filter-status').addEventListener('change', (e) => {
      this.filterStatus = e.target.value;
      this.loadConversations();
    });
    document.getElementById('sms-sort-by').addEventListener('change', (e) => {
      this.sortBy = e.target.value;
      this.loadConversations();
    });
    document.getElementById('sms-sort-order').addEventListener('change', (e) => {
      this.sortOrder = e.target.value;
      this.loadConversations();
    });

    // New SMS button
    document.getElementById('new-sms-btn').addEventListener('click', () => {
      this.showNewSmsModal();
    });

    // New SMS modal events
    document.getElementById('cancel-new-sms-btn').addEventListener('click', () => {
      this.hideNewSmsModal();
    });

    document.getElementById('new-sms-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.sendNewSms();
    });

    // Character count for new SMS
    document.getElementById('new-sms-message').addEventListener('input', (e) => {
      document.getElementById('new-sms-char-count').textContent = e.target.value.length;
    });

    // Message input events
    const messageInput = document.getElementById('sms-message-input');
    const sendButton = document.getElementById('send-sms-btn');

    messageInput.addEventListener('input', (e) => {
      const charCount = e.target.value.length;
      document.getElementById('sms-char-count').textContent = charCount;
      sendButton.disabled = !e.target.value.trim();
    });

    messageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    sendButton.addEventListener('click', () => {
      this.sendMessage();
    });

    // Close and archive buttons
    document.getElementById('close-conversation-btn').addEventListener('click', () => {
      this.closeCurrentConversation();
    });

    document.getElementById('archive-conversation-btn').addEventListener('click', () => {
      this.archiveCurrentConversation();
    });

    // Close modal when clicking outside
    document.getElementById('new-sms-modal').addEventListener('click', (e) => {
      if (e.target.id === 'new-sms-modal') {
        this.hideNewSmsModal();
      }
    });

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  subscribeToConversations() {
    // Subscribe to SMS conversations
    this.conversationsSubscription = supabase
      .channel('sms_conversations')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sms_conversations'
      }, (payload) => {
        // Check for new SMS conversations or updates (new messages)
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          // Notify the dashboard manager if it exists and has the method
          if (window.dashboardManager && typeof window.dashboardManager.notifyNewSmsReceived === 'function') {
            window.dashboardManager.notifyNewSmsReceived();
          }
        }
        // Reload conversations when any conversation changes
        this.loadConversations();
      })
      .subscribe();
  }

  subscribeToCurrentMessages() {
    // Unsubscribe from previous messages subscription
    if (this.messagesSubscription) {
      supabase.removeChannel(this.messagesSubscription);
    }

    if (!this.selectedConversationId) return;

    // Subscribe to new messages for the current conversation
    this.messagesSubscription = supabase
      .channel(`sms_messages:${this.selectedConversationId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'sms_messages',
        filter: `conversation_id=eq.${this.selectedConversationId}`
      }, (payload) => {
        const newMessage = payload.new;
        
        // Only add if it's not already in our current messages
        const exists = this.currentMessages.find(msg => msg.id === newMessage.id);
        if (!exists) {
          this.currentMessages.push(newMessage);
          this.renderMessages();
        }
      })
      .subscribe();
  }

  async loadConversations() {
    this.isLoading = true;
    
    try {
      let query = supabase
        .from('sms_conversations')
        .select(`
          *,
          customers (
            full_name,
            email
          )
        `);

      // Apply filter
      if (this.filterStatus !== 'all') {
        query = query.eq('status', this.filterStatus);
      }

      // Apply sorting
      query = query.order(this.sortBy, { ascending: this.sortOrder === 'asc' });
      query = query.order('created_at', { ascending: false }); // Secondary sort

      const { data: conversations, error } = await query;

      if (error) throw error;

      this.conversations = conversations || [];
      this.renderConversationsList();
    } catch (error) {
      this.showNotification('Error loading SMS conversations: ' + error.message, 'error');
    } finally {
      this.isLoading = false;
    }
  }

  renderConversationsList() {
    const conversationsList = document.getElementById('sms-conversations-list');
    
    if (this.conversations.length === 0) {
      conversationsList.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          <div class="text-lg mb-2">📱</div>
          <div>No SMS conversations found for current filters</div>
        </div>
      `;
      return;
    }

    conversationsList.innerHTML = this.conversations.map(conversation => 
      this.renderConversationCard(conversation)
    ).join('');

    // Bind conversation click events
    this.bindConversationEvents();
  }

  renderConversationCard(conversation) {
    const isActive = this.selectedConversationId === conversation.id;
    let statusColor = '';
    switch (conversation.status) {
      case 'open':
        statusColor = 'bg-green-100 text-green-800';
        break;
      case 'closed':
        statusColor = 'bg-gray-100 text-gray-800';
        break;
      case 'archived':
        statusColor = 'bg-blue-100 text-blue-800';
        break;
      default:
        statusColor = 'bg-gray-100 text-gray-800';
    }
    
    const timeAgo = this.formatTimeAgo(conversation.last_message_at || conversation.created_at);
    const displayName = conversation.customers?.full_name || conversation.phone_number;
    
    return `
      <div 
        class="conversation-card p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${isActive ? 'bg-blue-50 border-blue-200' : ''}"
        data-conversation-id="${conversation.id}"
      >
        <div class="flex justify-between items-start mb-2">
          <div class="font-medium text-gray-900 truncate">${displayName}</div>
          <span class="text-xs px-2 py-1 rounded-full ${statusColor}">${this.formatStatus(conversation.status)}</span>
        </div>
        <div class="text-sm text-gray-600 mb-1 truncate">${conversation.phone_number}</div>
        ${conversation.customers?.email ? `<div class="text-xs text-gray-500 mb-1 truncate">${conversation.customers.email}</div>` : ''}
        <div class="text-xs text-gray-500">${timeAgo}</div>
      </div>
    `;
  }

  bindConversationEvents() {
    document.querySelectorAll('.conversation-card').forEach(card => {
      card.addEventListener('click', (e) => {
        const conversationId = e.currentTarget.dataset.conversationId;
        this.selectConversation(conversationId);
      });
    });
  }

  async selectConversation(conversationId) {
    this.selectedConversationId = conversationId;
    this.renderConversationsList(); // Re-render to show active conversation
    
    // Subscribe to messages for this conversation
    this.subscribeToCurrentMessages();
    
    // Show loading in messages area
    const messagesArea = document.getElementById('sms-messages-area');
    messagesArea.innerHTML = `
      <div class="flex items-center justify-center h-full">
        <div class="text-center text-gray-500">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
          Loading messages...
        </div>
      </div>
    `;

    await this.loadMessages(conversationId);
    this.showSmsInterface();
  }

  async loadMessages(conversationId) {
    try {
      const { data: messages, error } = await supabase
        .from('sms_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      this.currentMessages = messages || [];
      this.renderMessages();
    } catch (error) {
      this.showNotification('Error loading messages: ' + error.message, 'error');
    }
  }

  renderMessages() {
    const messagesArea = document.getElementById('sms-messages-area');
    
    if (this.currentMessages.length === 0) {
      messagesArea.innerHTML = `
        <div class="flex items-center justify-center h-full text-gray-500">
          <div class="text-center">
            <div class="text-lg mb-2">📱</div>
            <div>No messages yet</div>
          </div>
        </div>
      `;
      return;
    }

    messagesArea.innerHTML = `
      <div class="space-y-4">
        ${this.currentMessages.map(message => this.renderMessage(message)).join('')}
      </div>
    `;

    // Auto-scroll to bottom
    messagesArea.scrollTop = messagesArea.scrollHeight;
  }

  renderMessage(message) {
    const isAgent = message.sender_type === 'agent';
    const timestamp = this.formatTimestamp(message.created_at);
    
    return `
      <div class="flex ${isAgent ? 'justify-end' : 'justify-start'}">
        <div class="max-w-xs lg:max-w-md">
          <div class="${isAgent ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200'} rounded-lg px-4 py-2 shadow-sm">
            <div class="text-sm">${this.escapeHtml(message.message_content)}</div>
          </div>
          <div class="text-xs text-gray-500 mt-1 ${isAgent ? 'text-right' : 'text-left'}">
            ${isAgent ? 'You' : 'Customer'} • ${timestamp}
          </div>
        </div>
      </div>
    `;
  }

  showSmsInterface() {
    const conversation = this.conversations.find(c => c.id === this.selectedConversationId);
    if (!conversation) return;

    // Show SMS header
    const smsHeader = document.getElementById('sms-header');
    const contactInfo = document.getElementById('sms-contact-info');
    const closeBtn = document.getElementById('close-conversation-btn');
    const archiveBtn = document.getElementById('archive-conversation-btn');
    const messageInputArea = document.getElementById('sms-message-input-area');
    
    const displayName = conversation.customers?.full_name || conversation.phone_number;
    contactInfo.innerHTML = `
      <div class="flex justify-between items-center">
        <div>
          <div class="font-medium text-gray-900">${displayName}</div>
          <div class="text-sm text-gray-600">${conversation.phone_number}</div>
          ${conversation.customers?.email ? `<div class="text-xs text-gray-500">${conversation.customers.email}</div>` : ''}
        </div>
        <div class="text-right">
          <div class="text-sm font-medium">Status: ${this.formatStatus(conversation.status)}</div>
          <div class="text-xs text-gray-500">Started: ${this.formatTimestamp(conversation.created_at)}</div>
        </div>
      </div>
    `;
    
    smsHeader.classList.remove('hidden');

    // Control button visibility based on conversation status
    const isConversationOpen = conversation.status === 'open';
    const isConversationArchived = conversation.status === 'archived';

    closeBtn.disabled = !isConversationOpen;
    archiveBtn.disabled = isConversationArchived;

    if (isConversationArchived) {
      archiveBtn.textContent = 'Archived';
      archiveBtn.className = 'bg-gray-500 text-white px-3 py-1 rounded text-sm cursor-not-allowed opacity-50';
    } else {
      archiveBtn.textContent = 'Archive';
      archiveBtn.className = 'bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600';
    }

    messageInputArea.classList.toggle('hidden', !isConversationOpen);
    
    // Focus message input if visible
    if (isConversationOpen) {
      document.getElementById('sms-message-input').focus();
    }
  }

  async sendMessage() {
    const messageInput = document.getElementById('sms-message-input');
    const sendButton = document.getElementById('send-sms-btn');
    const messageText = messageInput.value.trim();

    if (!messageText || !this.selectedConversationId) return;

    // Disable input while sending
    sendButton.disabled = true;
    messageInput.disabled = true;

    try {
      const conversation = this.conversations.find(c => c.id === this.selectedConversationId);
      if (!conversation) throw new Error('Conversation not found');

      // Send SMS via Edge Function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: conversation.phone_number,
          message: messageText,
          order_id: null // Not related to an order
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send SMS');
      }

      // Clear input
      messageInput.value = '';
      document.getElementById('sms-char-count').textContent = '0';
      
      // The message will be added via real-time subscription
      this.showNotification('SMS sent successfully!', 'success');

      // Refresh conversations to update last_message_at
      await this.loadConversations();
      
    } catch (error) {
      this.showNotification('Error sending SMS: ' + error.message, 'error');
    } finally {
      // Re-enable input
      sendButton.disabled = false;
      messageInput.disabled = false;
      messageInput.focus();
    }
  }

  showNewSmsModal() {
    document.getElementById('new-sms-modal').classList.remove('hidden');
    document.getElementById('new-sms-phone').focus();
  }

  hideNewSmsModal() {
    document.getElementById('new-sms-modal').classList.add('hidden');
    document.getElementById('new-sms-form').reset();
    document.getElementById('new-sms-char-count').textContent = '0';
  }

  async sendNewSms() {
    const phoneNumber = document.getElementById('new-sms-phone').value;
    const message = document.getElementById('new-sms-message').value;

    if (!phoneNumber || !message) return;

    try {
      // Send SMS via Edge Function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: phoneNumber,
          message: message,
          order_id: null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send SMS');
      }

      this.hideNewSmsModal();
      this.showNotification('SMS sent successfully!', 'success');
      
      // Refresh conversations
      await this.loadConversations();
      
    } catch (error) {
      this.showNotification('Error sending SMS: ' + error.message, 'error');
    }
  }

  async closeCurrentConversation() {
    if (!this.selectedConversationId) return;

    const conversation = this.conversations.find(c => c.id === this.selectedConversationId);
    if (!conversation) return;

    if (!confirm(`Close SMS conversation with ${conversation.phone_number}?`)) return;

    try {
      const { error } = await supabase
        .from('sms_conversations')
        .update({ status: 'closed' })
        .eq('id', this.selectedConversationId);

      if (error) throw error;

      this.showNotification('SMS conversation closed successfully', 'success');
      
      // Refresh conversations
      await this.loadConversations();
      
      // Update interface
      this.showSmsInterface();
      
    } catch (error) {
      this.showNotification('Error closing conversation: ' + error.message, 'error');
    }
  }

  async archiveCurrentConversation() {
    if (!this.selectedConversationId) return;

    const conversation = this.conversations.find(c => c.id === this.selectedConversationId);
    if (!conversation) return;

    if (conversation.status === 'archived') {
      this.showNotification('This conversation is already archived', 'warning');
      return;
    }

    if (!confirm(`Archive SMS conversation with ${conversation.phone_number}?`)) return;

    try {
      const { error } = await supabase
        .from('sms_conversations')
        .update({ status: 'archived' })
        .eq('id', this.selectedConversationId);

      if (error) throw error;

      this.showNotification('SMS conversation archived successfully', 'success');
      
      // Refresh conversations
      await this.loadConversations();
      
      // Update interface
      this.showSmsInterface();
      
    } catch (error) {
      this.showNotification('Error archiving conversation: ' + error.message, 'error');
    }
  }

  // Cleanup method to be called when switching views
  cleanup() {
    if (this.conversationsSubscription) {
      supabase.removeChannel(this.conversationsSubscription);
      this.conversationsSubscription = null;
    }
    
    if (this.messagesSubscription) {
      supabase.removeChannel(this.messagesSubscription);
      this.messagesSubscription = null;
    }
  }

  formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  }

  formatTimestamp(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatStatus(status) {
    switch (status) {
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      case 'archived': return 'Archived';
      default: return status;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showNotification(message, type = 'info') {
    const notificationDiv = document.getElementById('sms-notification');
    const contentDiv = document.getElementById('sms-notification-content');
    
    const colorClasses = {
      'info': 'bg-violet-50 text-violet-800 border border-violet-200',
      'success': 'bg-green-50 text-green-800 border border-green-200',
      'warning': 'bg-yellow-50 text-yellow-800 border border-yellow-200',
      'error': 'bg-red-50 text-red-800 border border-red-200'
    };

    notificationDiv.className = `fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg max-w-sm ${colorClasses[type] || colorClasses.info}`;
    contentDiv.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1 text-sm">${message}</div>
        <button 
          onclick="document.getElementById('sms-notification').classList.add('hidden')" 
          class="ml-4 text-current hover:opacity-75"
        >
          ✕
        </button>
      </div>
    `;
    
    notificationDiv.classList.remove('hidden');

    // Auto-hide success and info messages after 5 seconds
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        notificationDiv.classList.add('hidden');
      }, 5000);
    }
  }
}